const origin = (
  import.meta.env.VITE_AFTERRAIN_API_URL ?? window.location.origin
).replace(/\/$/, "");

type Numeric = number | string;

export type Overview = {
  totals: {
    players: number;
    new_players_24h: number;
    disabled_players: number;
    total_xp: Numeric;
    total_cash: Numeric;
    total_kills: Numeric;
    total_play_time_ms: Numeric;
  };
  attempts: {
    attempts: number;
    clears: number;
    deaths: number;
    avg_clear_ms: number;
  };
  push: {
    enabled_devices: number;
    ios_devices: number;
    android_devices: number;
  };
  recentAttempts: Array<Record<string, unknown>>;
  dropoffs: Array<Record<string, unknown>>;
};

export type AdminUser = {
  id: string;
  role: "player" | "owner";
  primary_email: string | null;
  created_at: string;
  disabled_at: string | null;
  gamer_tag: string;
  display_name: string | null;
  avatar_key: string;
  highest_area_id: number;
  checkpoint_area_id: number;
  xp: Numeric;
  cash: Numeric;
  kills: Numeric;
  play_time_ms: Numeric;
  push_devices: number;
  last_completed_area_id: number | null;
  next_area_id: number;
};

export type AdminUsersPage = {
  items: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type TrialUserStatus = "pending" | "emailed" | "failed" | "verified" | "trial_started";
export type TrialUserSource = "marketing" | "play_gate" | "app" | "blog";
export type ContactSegment = "launch" | "browser_trial";
export type ContactSort = "newest" | "oldest" | "name" | "recent_activity";

export type ContactSummary = {
  total: number;
  launch_contacts: number;
  browser_trial_requests: number;
  verified_emails: number;
  delivered_emails: number;
  opened_emails: number;
  delivery_failures: number;
  trial_starts: number;
  new_contacts_7d: number;
  unsubscribed: number;
};

export type TrialUser = {
  id: string;
  name: string | null;
  email: string;
  email_normalized: string;
  source: TrialUserSource;
  interest: string | null;
  funnel_status: TrialUserStatus;
  email_delivery_status: string | null;
  email_verification_sent_at: string | null;
  email_delivered_at: string | null;
  email_opened_at: string | null;
  email_failed_at: string | null;
  email_complained_at: string | null;
  email_last_event_at: string | null;
  email_last_event_type: string | null;
  email_verified_at: string | null;
  launch_updates_requested_at: string | null;
  launch_updates_confirmed_at: string | null;
  email_updates_unsubscribed_at: string | null;
  launch_updates_consent_version: string | null;
  trial_started_at: string | null;
  last_access_at: string | null;
  play_access_expires_at: string | null;
  created_at: string;
  updated_at: string;
  tracking_link_id: string | null;
  tracking_slug: string | null;
  tracking_platform: TrackingPlatform | null;
  tracking_content_title: string | null;
  tracking_campaign_name: string | null;
  tracking_video_url: string | null;
  clicked_at: string | null;
  optin_requested_at: string | null;
};

export type TrialUsersPage = {
  items: TrialUser[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  filters: {
    search: string;
    status: TrialUserStatus | "";
    source: TrialUserSource | "";
    segment: ContactSegment | "";
    sort: ContactSort;
  };
  summary: ContactSummary;
};

export type SoundSetting = {
  cue_key: string;
  display_name: string;
  category: string;
  asset_url: string;
  volume_basis_points: number;
  enabled: boolean;
  notes: string | null;
  updated_at: string;
};

export type NotificationCampaign = {
  id: string;
  title: string;
  body: string;
  target: string;
  status: string;
  audience: Record<string, unknown>;
  metrics: Record<string, unknown>;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
};

export type PushDeliveryStatus = {
  provider: "disabled" | "log" | "native";
  availablePlatforms: Array<"ios" | "android">;
};

export type Analytics = {
  events: Array<Record<string, unknown>>;
  areas: Array<Record<string, unknown>>;
  versions: Array<Record<string, unknown>>;
  platforms: Array<Record<string, unknown>>;
};

export type TrackingPlatform = "youtube" | "tiktok" | "instagram";
export type TrackingDestination = "marketing" | "browser_trial";

export type TrackingLink = {
  id: string;
  slug: string;
  platform: TrackingPlatform;
  content_title: string;
  campaign_name: string | null;
  video_url: string | null;
  destination: TrackingDestination;
  active: boolean;
  tracking_url: string;
  clicks: number;
  unique_visitors: number;
  preview_bots: number;
  optins: number;
  verified_emails: number;
  trial_starts: number;
  created_at: string;
  updated_at: string;
};

export type TrackingAnalytics = {
  items: TrackingLink[];
  totals: {
    clicks: number;
    unique_visitors: number;
    optins: number;
    verified_emails: number;
    trial_starts: number;
  };
  daily: Array<{
    day: string;
    clicks: number;
    verified_emails: number;
    trial_starts: number;
  }>;
};

export type LeaderboardRow = {
  metric: string;
  scope_key: string;
  value: Numeric;
  formatted_value?: string;
  projected_at: string;
  gamer_tag: string;
  avatar_key: string;
  primary_email: string | null;
  rank: number;
};

export class AdminApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
  ) {
    super(message);
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${origin}${path}`, {
    ...init,
    credentials: "include",
    headers: { "content-type": "application/json", ...init?.headers },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
    throw new AdminApiError(
      payload?.error?.message ?? "Afterrain admin could not complete the request.",
      response.status,
      payload?.error?.code ?? "request_failed",
    );
  }
  return response.json() as Promise<T>;
}

const idempotency = () => crypto.randomUUID();

export const getOverview = () => api<Overview>("/v1/admin/overview");
export const getUsers = (search = "", page = 1, pageSize = 50) =>
  api<AdminUsersPage>(
    `/v1/admin/users?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`,
  );
export const setUserEnabled = (userId: string, enabled: boolean) =>
  api<{ user: Pick<AdminUser, "id" | "role" | "primary_email" | "disabled_at"> }>(
    `/v1/admin/users/${encodeURIComponent(userId)}/status`,
    {
      method: "PATCH",
      headers: { "Idempotency-Key": idempotency() },
      body: JSON.stringify({ enabled }),
    },
  );
type ContactQuery = {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: TrialUserStatus | "";
  source?: TrialUserSource | "";
  segment?: ContactSegment | "";
  sort?: ContactSort;
};

const contactParams = (input: ContactQuery = {}) => {
  const params = new URLSearchParams({
    page: String(input.page ?? 1),
    pageSize: String(input.pageSize ?? 25),
    search: input.search ?? "",
    sort: input.sort ?? "newest",
  });
  if (input.status) params.set("status", input.status);
  if (input.source) params.set("source", input.source);
  if (input.segment) params.set("segment", input.segment);
  return params;
};

export const getTrialUsers = (input: ContactQuery = {}) =>
  api<TrialUsersPage>(`/v1/admin/trial-users?${contactParams(input).toString()}`);

export const exportContactsCsv = async (input: Omit<ContactQuery, "page" | "pageSize"> = {}) => {
  const response = await fetch(
    `${origin}/v1/admin/contacts/export?${contactParams(input).toString()}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
    throw new AdminApiError(
      payload?.error?.message ?? "Afterrain contacts could not be exported.",
      response.status,
      payload?.error?.code ?? "contact_export_failed",
    );
  }
  return response.blob();
};
export const getSounds = () => api<{ items: SoundSetting[] }>("/v1/admin/sounds");
export const updateSound = (cueKey: string, input: Partial<{
  assetUrl: string;
  volumeBasisPoints: number;
  enabled: boolean;
  notes: string | null;
}>) =>
  api<{ sound: SoundSetting }>(`/v1/admin/sounds/${encodeURIComponent(cueKey)}`, {
    method: "PATCH",
    headers: { "Idempotency-Key": idempotency() },
    body: JSON.stringify(input),
  });
export const getNotifications = () => api<{
  items: NotificationCampaign[];
  delivery: PushDeliveryStatus;
}>("/v1/admin/notifications?limit=30");
export const createNotification = (input: { title: string; body: string; target: string; status: "draft" | "queued" }) =>
  api<{ campaign: NotificationCampaign }>("/v1/admin/notifications", {
    method: "POST",
    headers: { "Idempotency-Key": idempotency() },
    body: JSON.stringify(input),
  });
export const getAnalytics = () => api<Analytics>("/v1/admin/analytics");
export const getTrackingLinks = (platform = "") =>
  api<TrackingAnalytics>(
    `/v1/admin/tracking-links?limit=100${platform ? `&platform=${encodeURIComponent(platform)}` : ""}`,
  );
export const createTrackingLink = (input: {
  slug: string;
  platform: TrackingPlatform;
  contentTitle: string;
  campaignName?: string | null;
  videoUrl?: string | null;
  destination: TrackingDestination;
}) =>
  api<{ link: TrackingLink }>("/v1/admin/tracking-links", {
    method: "POST",
    headers: { "Idempotency-Key": idempotency() },
    body: JSON.stringify(input),
  });
export const updateTrackingLink = (id: string, input: Partial<{
  platform: TrackingPlatform;
  contentTitle: string;
  campaignName: string | null;
  videoUrl: string | null;
  destination: TrackingDestination;
  active: boolean;
}>) =>
  api<{ link: TrackingLink }>(`/v1/admin/tracking-links/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Idempotency-Key": idempotency() },
    body: JSON.stringify(input),
  });
export const getLeaderboard = (metric: string, scopeKey: string) =>
  api<{ items: LeaderboardRow[] }>(
    `/v1/admin/leaderboards?metric=${encodeURIComponent(metric)}&scopeKey=${encodeURIComponent(scopeKey)}&limit=20`,
  );

export const loginWithPassword = (username: string, password: string) =>
  api<{ mustChangePassword: boolean }>("/v1/auth/password/login", {
    method: "POST",
    headers: { "Idempotency-Key": idempotency() },
    body: JSON.stringify({ username, password, deviceName: "Afterrain Admin" }),
  });

export const authStartUrl = (provider: "apple" | "google") =>
  `${origin}/v1/auth/${provider}/start?returnTo=${encodeURIComponent(window.location.href)}`;
